import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { User } from '@/lib/types'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface DeleteUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User | null
  onConfirm: () => void
  isDeleting: boolean
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  user,
  onConfirm,
  isDeleting,
}: DeleteUserDialogProps) {
  const { t } = useTranslation()
  const canDeleteUsers = useHasPermission('users.delete')

  if (!user) return null

  const isAzureUser = !!user.azureTenantId && !!user.azureObjectId

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-red-100 dark:bg-red-950">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <DialogTitle>
              {isAzureUser ? t('admin.users.deleteAzureUser') : t('admin.users.deleteUser')}
            </DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {t('admin.users.deleteWarning')}
            <br />
            <br />
            De gebruiker{' '}
            <strong>"{user.displayName || `${user.firstName} ${user.lastName}`}"</strong> wordt
            gedeactiveerd en kan daarna niet meer inloggen. De gebruikersgegevens en aangemaakte
            resources blijven bewaard voor de administratie.
            {isAzureUser && (
              <>
                <br />
                <br />
                <span className="text-orange-600 dark:text-orange-400 font-medium">
                  {t('admin.users.deleteAzureUserWarning')}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {!canDeleteUsers && (
          <div className="mb-4 p-3 text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-md dark:bg-orange-950/20 dark:text-orange-400 dark:border-orange-800">
            {t('admin.users.noPermissionToDelete')}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            {t('admin.users.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting || !canDeleteUsers}
          >
            {isDeleting ? t('admin.users.delete') + '...' : t('admin.users.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
